// Read/write helpers for the board's `meta` Y.Map. Every retro setter goes
// through a `doc.transact` with a `LOCAL_ORIGIN_RETRO` tag so the undo
// manager ignores meta changes (you don't want Cmd+Z to flip the phase
// back). The map stores primitive values directly except `timer`, which is
// a small POJO serialized via JSON.stringify to dodge Y.Map-in-Y.Map fanout.

import * as Y from 'yjs';
import type { BoardMode, RetroMeta, RetroPhase, RetroTimer } from '@frame/shared';
import { DEFAULT_VOTE_BUDGET } from '@frame/shared';

export const LOCAL_ORIGIN_RETRO = 'local-retro';

export function getMetaMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>('meta');
}

// Snapshot the current meta. Returns sensible defaults when fields are
// absent (mode defaults to 'freeform' so existing boards open unchanged).
export function readMeta(doc: Y.Doc): RetroMeta {
  const map = getMetaMap(doc);
  const mode = (map.get('mode') as BoardMode | undefined) ?? 'freeform';
  const phase = map.get('phase') as RetroPhase | undefined;
  const revealed = (map.get('revealed') as boolean | undefined) ?? false;
  const voteBudget = (map.get('voteBudget') as number | undefined) ?? DEFAULT_VOTE_BUDGET;
  const timerRaw = map.get('timer') as string | undefined;
  let timer: RetroTimer | undefined;
  if (timerRaw) {
    try {
      const parsed = JSON.parse(timerRaw) as RetroTimer;
      // Defensive: only accept well-shaped timer payloads. Malformed JSON
      // (peer running a future version with extra fields, etc.) falls back
      // to "no timer" rather than crashing the renderer.
      if (
        typeof parsed.endsAt === 'number' &&
        typeof parsed.durationMs === 'number' &&
        typeof parsed.paused === 'boolean'
      ) {
        timer = parsed;
      }
    } catch {
      // Ignored — corrupt timer payload, treat as no timer.
    }
  }
  return { mode, phase, revealed, voteBudget, timer };
}

export function setMode(doc: Y.Doc, mode: BoardMode): void {
  doc.transact(() => {
    getMetaMap(doc).set('mode', mode);
    // When flipping INTO retro, seed phase=lobby if absent so the phase bar
    // has something to render. When flipping OUT, leave phase alone — going
    // back to retro should land where the facilitator left off.
    if (mode === 'retro' && !getMetaMap(doc).has('phase')) {
      getMetaMap(doc).set('phase', 'lobby');
    }
  }, LOCAL_ORIGIN_RETRO);
}

export function setPhase(doc: Y.Doc, phase: RetroPhase): void {
  doc.transact(() => {
    getMetaMap(doc).set('phase', phase);
    // Entering `reveal` flips the anonymous overlay off automatically. The
    // facilitator can still toggle it back via setRevealed if they want.
    if (phase === 'reveal' || phase === 'group' || phase === 'vote' || phase === 'discuss' || phase === 'actions') {
      getMetaMap(doc).set('revealed', true);
    }
    if (phase === 'write' || phase === 'lobby') {
      getMetaMap(doc).set('revealed', false);
    }
  }, LOCAL_ORIGIN_RETRO);
}

export function setRevealed(doc: Y.Doc, revealed: boolean): void {
  doc.transact(() => {
    getMetaMap(doc).set('revealed', revealed);
  }, LOCAL_ORIGIN_RETRO);
}

export function setVoteBudget(doc: Y.Doc, budget: number): void {
  // Clamp to a sane range — 0 disables voting; > 20 isn't a useful retro.
  const clamped = Math.max(0, Math.min(20, Math.round(budget)));
  doc.transact(() => {
    getMetaMap(doc).set('voteBudget', clamped);
  }, LOCAL_ORIGIN_RETRO);
}

// Start a fresh countdown from `now`. Overwrites any in-flight timer.
export function startTimer(doc: Y.Doc, durationMs: number, now: number = Date.now()): void {
  const timer: RetroTimer = {
    endsAt: now + durationMs,
    durationMs,
    paused: false,
  };
  writeTimer(doc, timer);
}

// Pause an active timer. No-op if no timer or already paused.
export function pauseTimer(doc: Y.Doc, now: number = Date.now()): void {
  const meta = readMeta(doc);
  if (!meta.timer || meta.timer.paused) return;
  writeTimer(doc, { ...meta.timer, paused: true, pausedAt: now });
}

// Resume a paused timer; shifts endsAt by the pause duration so the
// countdown picks up where it left off.
export function resumeTimer(doc: Y.Doc, now: number = Date.now()): void {
  const meta = readMeta(doc);
  if (!meta.timer || !meta.timer.paused) return;
  const pausedFor = now - (meta.timer.pausedAt ?? now);
  writeTimer(doc, {
    ...meta.timer,
    paused: false,
    pausedAt: undefined,
    endsAt: meta.timer.endsAt + pausedFor,
  });
}

export function clearTimer(doc: Y.Doc): void {
  doc.transact(() => {
    getMetaMap(doc).delete('timer');
  }, LOCAL_ORIGIN_RETRO);
}

function writeTimer(doc: Y.Doc, timer: RetroTimer): void {
  doc.transact(() => {
    // pausedAt: undefined is dropped by JSON.stringify, which is what we
    // want — an absent key on the next read becomes undefined again.
    getMetaMap(doc).set('timer', JSON.stringify(timer));
  }, LOCAL_ORIGIN_RETRO);
}
